import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { toast } from 'sonner';
import { Icon } from '@iconify/react';
import { useStudioStore } from '@/store/useStudioStore';
import { projectApi } from '@/api/project';
import { fileApi } from '@/api/file';
import { ossApi } from '@/api/oss';
import {
  SEO_DESCRIPTION_MAX_LENGTH,
  SEO_TITLE_MAX_LENGTH,
  buildDraftPreviewHtml,
  extractMetadataFromHtml,
  normalizeSeoMetadataInput,
  updateMetadataInHtml,
} from '@/lib/domUtils';
import { resolveEditableDraft } from '@/lib/draftUtils';
import { isSpecialProjectType } from '@/lib/projectUtils.ts';
import { trackStudioEvent } from '@/lib/studioTracking';
import { getTrackingErrorCode } from '@/lib/trackingUtils';
import type { ProjectGeneralSettingVO, SaveSeoConfigRequest } from '@/types/project';

type GeneralSettingsForm = {
  allowIndexing: boolean;
  description: string;
  euSupportEnabled: boolean;
  faviconUrl: string;
  keywords: string;
  ogDescription: string;
  ogImageUrl: string;
  ogTitle: string;
  title: string;
};

const INITIAL_GENERAL_SETTINGS_FORM: GeneralSettingsForm = {
  title: '',
  description: '',
  keywords: '',
  ogTitle: '',
  ogDescription: '',
  ogImageUrl: '',
  allowIndexing: true,
  faviconUrl: '',
  euSupportEnabled: false,
};

const SHOW_OPEN_GRAPH_SETTINGS = true;
const SHOW_INDEXING_SETTINGS = true;
const MIN_FAVICON_SIZE = 32;

const getProjectConfigParams = (currentId: string | null, currentProjectId: string | null) => {
  if (currentId) {
    return { userProjectId: Number(currentId) };
  }
  if (currentProjectId) {
    return { projectId: Number(currentProjectId) };
  }
  return null;
};

const hasPersistedMetadata = (config: ProjectGeneralSettingVO | null) =>
  Boolean(
    config &&
    (config.title ||
      config.description ||
      config.keywords ||
      config.ogTitle ||
      config.ogDescription ||
      config.ogImageUrl ||
      config.faviconUrl ||
      config.allowIndexing === 0 ||
      config.euSupportEnabled === 1),
  );

const normalizeGeneralSettingsInput = (
  metadata: Partial<GeneralSettingsForm>,
): GeneralSettingsForm => {
  const normalizedSeo = normalizeSeoMetadataInput({
    title: metadata.title,
    description: metadata.description,
    keywords: metadata.keywords,
    ogTitle: metadata.ogTitle,
    ogDescription: metadata.ogDescription,
    ogImageUrl: metadata.ogImageUrl,
    allowIndexing: metadata.allowIndexing,
    faviconUrl: metadata.faviconUrl,
  });

  return {
    ...normalizedSeo,
    euSupportEnabled: metadata.euSupportEnabled ?? false,
  };
};

const mapConfigToFormState = (config: ProjectGeneralSettingVO): GeneralSettingsForm =>
  normalizeGeneralSettingsInput({
    title: config.title,
    description: config.description,
    keywords: config.keywords,
    ogTitle: config.ogTitle,
    ogDescription: config.ogDescription,
    ogImageUrl: config.ogImageUrl,
    allowIndexing: config.allowIndexing === 1,
    faviconUrl: config.faviconUrl,
    euSupportEnabled: config.euSupportEnabled === 1,
  });

const buildMetadataHtmlPayload = (form: GeneralSettingsForm) => ({
  title: form.title,
  description: form.description,
  keywords: form.keywords,
  ogTitle: form.ogTitle || form.title,
  ogDescription: form.ogDescription || form.description,
  ogImageUrl: form.ogImageUrl,
  allowIndexing: form.allowIndexing,
  faviconUrl: form.faviconUrl,
});

const readImageDimensions = (file: File): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to read image dimensions.'));
    };

    image.src = objectUrl;
  });

export const StudioSettingsMetadata = () => {
  const { currentId, currentProjectId, currentProjectTypeId, generatedHtml, setCurrentDraftMeta } =
    useStudioStore();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [form, setForm] = useState<GeneralSettingsForm>(INITIAL_GENERAL_SETTINGS_FORM);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const params = getProjectConfigParams(currentId, currentProjectId);
    setForm(INITIAL_GENERAL_SETTINGS_FORM);
    if (!params) return;

    let cancelled = false;

    const loadMetadataFromCurrentSource = async () => {
      if (generatedHtml.trim()) {
        return generatedHtml;
      }

      if (isSpecialProjectType(currentProjectTypeId)) {
        if (!currentProjectId) return '';

        try {
          const files = await fileApi.listFiles({
            projectId: Number(currentProjectId),
            path: 'dist',
          });
          const indexFile = files.find((file) => file.name === 'index.html' && !file.directory);
          if (!indexFile) return '';

          const targetPath = indexFile.path.startsWith('dist/')
            ? indexFile.path
            : `dist/${indexFile.path}`;
          const fileContent = await fileApi.readFile({
            projectId: Number(currentProjectId),
            path: targetPath,
          });
          return fileContent.content || '';
        } catch {
          return '';
        }
      }

      if (!currentId) return '';

      try {
        const { editableDraft } = await resolveEditableDraft(Number(currentId));
        return editableDraft?.content || '';
      } catch {
        return '';
      }
    };

    const loadSavedGeneralConfig = async () => {
      const [generalResult, seoResult] = await Promise.allSettled([
        projectApi.getGeneralSettingDetail(params),
        projectApi.getSeoDetail(params),
      ]);

      const generalConfig = generalResult.status === 'fulfilled' ? generalResult.value : null;
      const seoConfig = seoResult.status === 'fulfilled' ? seoResult.value : null;

      return generalConfig || seoConfig;
    };

    const loadMetadata = async () => {
      const savedConfig = await loadSavedGeneralConfig();
      if (cancelled) return;

      if (hasPersistedMetadata(savedConfig)) {
        setForm(mapConfigToFormState(savedConfig!));
        return;
      }

      const currentHtml = await loadMetadataFromCurrentSource();
      if (cancelled) return;
      if (!currentHtml) {
        setForm(INITIAL_GENERAL_SETTINGS_FORM);
        return;
      }

      setForm(
        normalizeGeneralSettingsInput({
          ...extractMetadataFromHtml(currentHtml),
          euSupportEnabled: false,
        }),
      );
    };

    void loadMetadata();

    return () => {
      cancelled = true;
    };
  }, [currentId, currentProjectId, currentProjectTypeId, generatedHtml]);

  const updateFormField = <K extends keyof GeneralSettingsForm>(
    key: K,
    value: GeneralSettingsForm[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleFaviconUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const isSupportedImage = file.type.startsWith('image/') || /\.ico$/i.test(file.name);
    if (!isSupportedImage) {
      toast.error('Upload a PNG, JPG, SVG, WebP, or ICO file.');
      return;
    }

    try {
      const { width, height } = await readImageDimensions(file);
      if (width < MIN_FAVICON_SIZE || height < MIN_FAVICON_SIZE) {
        toast.error(
          `Favicon must be at least ${MIN_FAVICON_SIZE}x${MIN_FAVICON_SIZE} px. Current image is ${width}x${height} px.`,
        );
        return;
      }
    } catch (error) {
      toast.error((error as { message?: string })?.message || 'Failed to read favicon image.');
      return;
    }

    setFaviconUploading(true);
    try {
      const { uploadUrl, viewUrl } = await ossApi.generatePresignedUrl({
        filename: file.name,
        contentType: file.type || (/\.ico$/i.test(file.name) ? 'image/x-icon' : 'image/png'),
      });
      await ossApi.uploadFile(uploadUrl, file);
      updateFormField('faviconUrl', viewUrl);
    } catch (error) {
      toast.error((error as { message?: string })?.message || 'Failed to upload favicon.');
    } finally {
      setFaviconUploading(false);
    }
  };

  const persistUpdatedSourceHtml = async (normalizedForm: GeneralSettingsForm) => {
    const htmlMetadata = buildMetadataHtmlPayload(normalizedForm);

    if (isSpecialProjectType(currentProjectTypeId)) {
      if (!currentProjectId) return;

      const files = await fileApi.listFiles({
        projectId: Number(currentProjectId),
        path: 'dist',
      });
      const indexFile = files.find((file) => file.name === 'index.html' && !file.directory);
      if (!indexFile) return;

      const targetPath = indexFile.path.startsWith('dist/')
        ? indexFile.path
        : `dist/${indexFile.path}`;
      const fileContent = await fileApi.readFile({
        projectId: Number(currentProjectId),
        path: targetPath,
      });

      await fileApi.writeFile({
        projectId: Number(currentProjectId),
        path: targetPath,
        content: updateMetadataInHtml(fileContent.content, htmlMetadata),
      });
      return;
    }

    if (!currentId) return;

    const { editableDraft, saveBaseVersion } = await resolveEditableDraft(Number(currentId));
    if (!editableDraft?.content) return;

    const updatedContent = updateMetadataInHtml(editableDraft.content, htmlMetadata);
    const savedDraft = await projectApi.saveDraft({
      userProjectId: Number(currentId),
      content: updatedContent,
      changeNote: 'Updated general settings',
      draftStatus: 'CONFIRMED',
      baseVersion: saveBaseVersion ?? editableDraft.version,
    });

    if (savedDraft) {
      setCurrentDraftMeta(savedDraft.id, savedDraft.version, savedDraft.draftStatus);
    }

    useStudioStore.setState({
      generatedHtml: buildDraftPreviewHtml(savedDraft?.content || updatedContent),
      isBuilding: false,
    });
  };

  const handleSave = async () => {
    const params = getProjectConfigParams(currentId, currentProjectId);
    if (!params) return;

    const normalizedForm = normalizeGeneralSettingsInput(form);
    const trackingData = {
      titleLength: normalizedForm.title.length,
      descriptionLength: normalizedForm.description.length,
      hasKeywords: Boolean(normalizedForm.keywords.trim()),
      allowIndexing: normalizedForm.allowIndexing,
      hasOgImage: Boolean(normalizedForm.ogImageUrl.trim()),
      hasFavicon: Boolean(normalizedForm.faviconUrl.trim()),
      euSupportEnabled: normalizedForm.euSupportEnabled,
    };

    if (!normalizedForm.title.trim()) {
      trackStudioEvent('studio_metadata_save_result', {
        result: 'validation_failed',
        ...trackingData,
        errorCode: 'missing_title',
      });
      toast.error('Title cannot be empty.');
      return;
    }

    const payload: SaveSeoConfigRequest = {
      ...params,
      title: normalizedForm.title,
      description: normalizedForm.description,
      keywords: normalizedForm.keywords,
      ogTitle: normalizedForm.ogTitle,
      ogDescription: normalizedForm.ogDescription,
      ogImageUrl: normalizedForm.ogImageUrl,
      allowIndexing: normalizedForm.allowIndexing ? 1 : 0,
      faviconUrl: normalizedForm.faviconUrl || undefined,
      euSupportEnabled: normalizedForm.euSupportEnabled ? 1 : 0,
    };

    setSaving(true);
    try {
      try {
        await projectApi.saveGeneralSetting(payload, { suppressErrorToast: true });
        void projectApi.saveSeoConfig(payload, { suppressErrorToast: true }).catch(() => null);
      } catch {
        await projectApi.saveSeoConfig(payload, { suppressErrorToast: true });
      }

      await persistUpdatedSourceHtml(normalizedForm);

      setForm(normalizedForm);
      trackStudioEvent('studio_metadata_save_result', {
        result: 'success',
        ...trackingData,
      });
      toast.success('Page details saved successfully');
    } catch (error) {
      trackStudioEvent('studio_metadata_save_result', {
        result: 'failure',
        ...trackingData,
        errorCode: getTrackingErrorCode(error),
      });
      toast.error((error as { message?: string })?.message || 'Failed to save page details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cfg-section active animate-in fade-in duration-200">
      <div className="cfg-sec-title mb-[4px] text-[15px] font-[800] tracking-[-0.3px] text-[#0D0D1A]">
        Page details
      </div>
      <div className="cfg-sec-sub mb-[18px] text-[12.5px] leading-[1.6] text-[#0D0D1A]/56">
        Prepare how this page appears in search, sharing previews, and browser tabs.
      </div>

      <div className="mb-[12px] text-[13px] font-[800] text-[#0D0D1A]">Search preview</div>

      <div className="cfg-field mb-[14px]">
        <label className="cfg-label mb-[5px] block text-[12px] font-[700] text-[#0D0D1A]/56">
          Title <span className="font-[400] text-[#0D0D1A]/40">(max 60 chars)</span>
        </label>
        <input
          className="cfg-input h-[38px] w-full rounded-[8px] border border-[#0D0D1A]/10 px-3 text-[13.5px] transition-all outline-none focus:border-[#6D28D9]/40"
          type="text"
          maxLength={SEO_TITLE_MAX_LENGTH}
          placeholder="e.g. DevTools Pro - Debug Faster, Ship Smarter"
          value={form.title}
          onChange={(event) => updateFormField('title', event.target.value)}
        />
        <div className="mt-1 text-right text-[11px] text-[#0D0D1A]/40">
          {form.title.length} / {SEO_TITLE_MAX_LENGTH}
        </div>
      </div>

      <div className="cfg-field mb-[14px]">
        <label className="cfg-label mb-[5px] block text-[12px] font-[700] text-[#0D0D1A]/56">
          Description <span className="font-[400] text-[#0D0D1A]/40">(max 160 chars)</span>
        </label>
        <textarea
          className="cfg-input w-full rounded-[8px] border border-[#0D0D1A]/10 p-3 text-[13.5px] transition-all outline-none focus:border-[#6D28D9]/40"
          rows={3}
          maxLength={SEO_DESCRIPTION_MAX_LENGTH}
          placeholder="A concise summary shown in search results..."
          value={form.description}
          onChange={(event) => updateFormField('description', event.target.value)}
        />
        <div className="mt-1 text-right text-[11px] text-[#0D0D1A]/40">
          {form.description.length} / {SEO_DESCRIPTION_MAX_LENGTH}
        </div>
      </div>

      <div className="cfg-field mb-[14px]">
        <label className="cfg-label mb-[5px] block text-[12px] font-[700] text-[#0D0D1A]/56">
          Keywords <span className="font-[400] text-[#0D0D1A]/40">(comma-separated)</span>
        </label>
        <input
          className="cfg-input h-[38px] w-full rounded-[8px] border border-[#0D0D1A]/10 px-3 text-[13.5px] transition-all outline-none focus:border-[#6D28D9]/40"
          type="text"
          placeholder="e.g. developer tools, API monitoring, debugging"
          value={form.keywords}
          onChange={(event) => updateFormField('keywords', event.target.value)}
        />
        <div className="mt-1 text-[11px] leading-[1.4] text-[#0D0D1A]/40">
          Keywords have minimal SEO impact today but may help with internal search and
          categorization.
        </div>
      </div>

      {SHOW_OPEN_GRAPH_SETTINGS ? (
        <>
          <div className="my-[18px] border-t border-[#0D0D1A]/10" />
          <div className="mb-[4px] text-[13px] font-[800] text-[#0D0D1A]">Share preview</div>
          <div className="mb-[14px] text-[12.5px] leading-[1.6] text-[#0D0D1A]/56">
            Control the preview people see when this page is shared.
          </div>

          <div className="cfg-field mb-[14px]">
            <label className="cfg-label mb-[5px] block text-[12px] font-[700] text-[#0D0D1A]/56">
              OG Title
            </label>
            <input
              className="cfg-input h-[38px] w-full rounded-[8px] border border-[#0D0D1A]/10 px-3 text-[13.5px] transition-all outline-none focus:border-[#6D28D9]/40"
              type="text"
              placeholder="Defaults to page title if empty"
              value={form.ogTitle}
              onChange={(event) => updateFormField('ogTitle', event.target.value)}
            />
          </div>

          <div className="cfg-field mb-[14px]">
            <label className="cfg-label mb-[5px] block text-[12px] font-[700] text-[#0D0D1A]/56">
              OG Description
            </label>
            <textarea
              className="cfg-input w-full rounded-[8px] border border-[#0D0D1A]/10 p-3 text-[13.5px] transition-all outline-none focus:border-[#6D28D9]/40"
              rows={2}
              placeholder="Defaults to meta description if empty"
              value={form.ogDescription}
              onChange={(event) => updateFormField('ogDescription', event.target.value)}
            />
          </div>

          <div className="cfg-field mb-[14px]">
            <label className="cfg-label mb-[5px] block text-[12px] font-[700] text-[#0D0D1A]/56">
              OG Image URL
            </label>
            <input
              className="cfg-input h-[38px] w-full rounded-[8px] border border-[#0D0D1A]/10 px-3 text-[13.5px] transition-all outline-none focus:border-[#6D28D9]/40"
              type="url"
              placeholder="https://yourdomain.com/og-image.png"
              value={form.ogImageUrl}
              onChange={(event) => updateFormField('ogImageUrl', event.target.value)}
            />
            <div className="mt-2 rounded-[9px] border border-[#0891B2]/20 bg-[#0891B2]/[0.08] p-[11px_13px] text-[12.5px] leading-[1.6] text-[#0E7490]">
              Recommended size: 1200 x 630 px. Leave empty to auto-generate from your page.
            </div>
          </div>
        </>
      ) : null}

      {SHOW_INDEXING_SETTINGS ? (
        <>
          <div className="my-[18px] border-t border-[#0D0D1A]/10" />
          <div className="mb-[12px] text-[13px] font-[800] text-[#0D0D1A]">Search visibility</div>

          <div className="flex items-center justify-between gap-[12px] rounded-[10px] border border-[#0D0D1A]/10 bg-white px-[14px] py-[12px]">
            <div className="min-w-0">
              <div className="text-[13px] font-[600] text-[#0D0D1A]">Show this page in search</div>
              <div className="mt-[2px] text-[11.5px] text-[#0D0D1A]/40">
                Allow search engines to include this page in results
              </div>
            </div>
            <button
              type="button"
              aria-pressed={form.allowIndexing}
              onClick={() => updateFormField('allowIndexing', !form.allowIndexing)}
              className={`relative h-[21px] w-[38px] shrink-0 rounded-full transition-colors duration-200 after:absolute after:top-[3px] after:left-[3px] after:h-[15px] after:w-[15px] after:rounded-full after:bg-white after:shadow-[0_1px_3px_rgba(0,0,0,0.2)] after:transition-transform after:duration-200 ${
                form.allowIndexing ? 'bg-[#6D28D9] after:translate-x-[17px]' : 'bg-black/[0.15]'
              }`}
            />
          </div>
        </>
      ) : null}

      <div className="my-[18px] border-t border-[#0D0D1A]/10" />
      <div className="mb-[4px] text-[13px] font-[800] text-[#0D0D1A]">Browser icon</div>
      <div className="mb-[14px] text-[12.5px] leading-[1.6] text-[#0D0D1A]/56">
        The small icon shown in browser tabs and bookmarks.
      </div>

      <div className="flex flex-col items-start gap-[8px]">
        {form.faviconUrl ? (
          <img
            src={form.faviconUrl}
            alt="favicon"
            className="h-[40px] w-[40px] rounded-[6px] border border-[#0D0D1A]/10 object-cover"
          />
        ) : null}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={faviconUploading}
          className="inline-flex items-center gap-[6px] rounded-[9px] bg-[linear-gradient(135deg,#6D28D9,#0891B2)] px-[14px] py-[7px] text-[12px] font-[700] text-white transition-opacity hover:opacity-[0.88] disabled:opacity-50"
        >
          <Icon icon="fa6-solid:upload" className="text-[12px]" />
          {faviconUploading ? 'Uploading...' : 'Upload image'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.ico"
          className="hidden"
          onChange={handleFaviconUpload}
        />
        <div className="text-[11px] text-[#0D0D1A]/40">
          PNG, JPG, SVG, WebP, or ICO. At least 32x32 px. Square images work best.
        </div>
      </div>

      <div className="my-[18px] border-t border-[#0D0D1A]/10" />
      <div className="mb-[4px] text-[13px] font-[800] text-[#0D0D1A]">Regional privacy support</div>
      <div className="mb-[14px] text-[12.5px] leading-[1.6] text-[#0D0D1A]/56">
        Turn this on when the landing page is intended for visitors in the European Union.
      </div>

      <div className="flex items-center justify-between gap-[12px] rounded-[10px] border border-[#0D0D1A]/10 bg-white px-[14px] py-[12px]">
        <div className="min-w-0">
          <div className="text-[13px] font-[600] text-[#0D0D1A]">Landing page used in the EU</div>
          <div className="mt-[2px] text-[11.5px] text-[#0D0D1A]/40">
            Enable EU-facing support such as consent-aware tracking and region-ready page handling.
          </div>
        </div>
        <button
          type="button"
          aria-pressed={form.euSupportEnabled}
          onClick={() => updateFormField('euSupportEnabled', !form.euSupportEnabled)}
          className={`relative h-[21px] w-[38px] shrink-0 rounded-full transition-colors duration-200 after:absolute after:top-[3px] after:left-[3px] after:h-[15px] after:w-[15px] after:rounded-full after:bg-white after:shadow-[0_1px_3px_rgba(0,0,0,0.2)] after:transition-transform after:duration-200 ${
            form.euSupportEnabled ? 'bg-[#6D28D9] after:translate-x-[17px]' : 'bg-black/[0.15]'
          }`}
        />
      </div>

      <div className="mt-[18px]">
        <button
          className="cfg-save-btn inline-flex h-[36px] cursor-pointer items-center justify-center rounded-[9px] bg-[linear-gradient(135deg,#6D28D9,#0891B2)] px-[16px] text-[12.5px] font-[700] text-white transition-opacity hover:opacity-[0.88] disabled:opacity-50"
          onClick={handleSave}
          disabled={saving || faviconUploading}
        >
          {saving ? 'Saving...' : 'Save page details'}
        </button>
      </div>
    </div>
  );
};
